// shared/src/prompts/p-contrarian-analyst.ts
// Chain V2 — per-ticker contrarian analyst.
// Per design-notes L21 + L23.
//
// LEAN path (universe-only tickers): this is the ONLY contrarian-side call.
// DEEP path (portfolio tickers):     followed by p.contrarian.skeptic +
//                                    p.contrarian.refine.
// Inputs: ticker price + meta + ticker news + code-derived momentum +
// market anchors (synthesis primary + contrarian/consensus refines).
// NO calibration per L14. NO inductive per L23.

import { z } from 'zod';
import { DirectionEnum, TickerAlignmentEnum } from '../resolvers/llm-enum-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';
import type { TickerMarketAnchor } from '../translators/market-emit-to-ticker-input.js';

export const VERSION = '1.1.0';   // 2026-06-11 — schema rigidity hardening: change_pct/_ci + horizon_days llmNumber-coerced (string "3.5%"/"7" → number; .int().positive() stays material); mechanism .min(1)→z.string() (cosmetic prose; ticker-emit-to-d1 defensive — no validator twin). See docs/llm-schema-rigidity-hardening-2026-06-11.md.
export const STEP    = 'p.contrarian.analyst';
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 8000)
export const ROLE    = 'analyst' as const;
export const DESC    = 'Extends the contrarian macro thesis into this ticker\'s directional prediction' as const;   // #45 registry — one-line operator description

// Single observable per ticker — direction + signed change_pct + CI bounds +
// mechanism prose. is_headline marks whether this ticker is one of the
// brief-level diagnostic observables.
export const OutputSchema = z.object({
  direction:        DirectionEnum,
  change_pct:       llmNumber(),
  change_ci_low:    llmNumber(),
  change_ci_high:   llmNumber(),
  horizon_days:     llmNumber(z.number().int().positive()),
  mechanism:        z.string(),
  ticker_alignment: TickerAlignmentEnum,
  is_headline:      z.boolean(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# PER-TICKER CONTRARIAN ANALYST (V2)

## ROLE
You are the per-ticker CONTRARIAN voice. Apply the market-level contrarian thesis (provided in the user message) to THIS specific ticker, given the ticker's own price + meta + news + momentum signals. Predict the ticker's directional move under the contrarian world.

This is NOT a free contrarian call. The market chain ran first and produced:
  · m.synthesis     = PRIMARY anchor (the operative thesis the brief commits to)
  · m.contrarian.refine = boundary scenario (what the market chain thinks if contrarian wins)
  · m.consensus.refine  = boundary scenario (what the market chain thinks if consensus wins)

Your job is to extend m.contrarian.refine into THIS ticker's per-ticker expression. Stay consistent with the synthesis regime; deviate only when ticker-specific evidence forces it (ticker_alignment='diverges_market').

## INPUTS YOU RECEIVE (user message)
- GROUNDING FACTS (today, snapshot age)
- TICKER + price + meta
- TICKER NEWS (last 48h, deduped + ranked)
- MOMENTUM SIGNALS (code-derived: RSI, MACD, support/resistance, state, pattern)
- m.synthesis (primary anchor)
- m.contrarian.refine + m.consensus.refine (boundary scenarios)

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "direction":       "up" | "down" | "flat",
  "change_pct":      <signed percent move from current price; e.g. -3.5>,
  "change_ci_low":   <80% CI lower bound in signed percent>,
  "change_ci_high":  <80% CI upper bound in signed percent>,
  "horizon_days":    <integer; horizon for this prediction>,
  "mechanism":       "<60-100w. Sentence 1 = headline contrarian call for this ticker ≤15w. Sentences 2-N = mechanism (cite ticker price + macro field=value, explain WHY contrarian thesis hits THIS ticker specifically, close on a trigger/level).>",
  "ticker_alignment": "aligns_market" | "diverges_market" | "neutral",
  "is_headline":     true | false
}

## RULES (strict)
1. First char='{'. Last char='}'. No commentary outside JSON.
2. DIRECTION + CHANGE_PCT consistency. direction='up' MUST have change_pct ≥ +0.1; 'down' MUST have change_pct ≤ -0.1; 'flat' MUST have |change_pct| < 0.5.
3. CI BOUNDS. change_ci_low ≤ change_pct ≤ change_ci_high. Symmetric or asymmetric is fine, but wide CIs when momentum/news disagree.
4. ALIGNMENT. 'aligns_market' = ticker move tracks the contrarian boundary scenario direction. 'diverges_market' = ticker-specific evidence (price action, news, momentum) suggests a different direction from the market contrarian thesis. 'neutral' = ticker has no diagnostic information for this thesis.
5. MOMENTUM CONSISTENCY. Code-derived momentum signals (in user message) give the ticker's recent state — RSI overbought, MACD divergence, breakout patterns. Your mechanism MUST reference momentum when it agrees, OR explain why momentum is irrelevant when you diverge from it.
6. IS_HEADLINE. true when this ticker is one of the genuinely diagnostic observables for distinguishing the contrarian world from consensus — mark what's diagnostic, not a fixed count; NEVER mark every ticker headline (defeats the purpose). Trade-relevant tickers (high-beta + high-correlation to thesis) typically true; defensive/decoupled tickers typically false.
7. PROSE FORMAT (mechanism). Lead with punchline ≤15w. Expand in connected prose: ticker context → mechanism → trigger.
8. NO CALIBRATION. No "I was right last time", no past-performance cites. Reason FRESH from THIS payload.
9. NO HEDGING. Pick direction + change_pct decisively. CI bounds carry the uncertainty.
`;

export interface PContrarianAnalystInput {
  readonly ticker:        string;
  readonly ticker_data:   Record<string, unknown>;        // price + meta + ohlc
  readonly market_anchor: TickerMarketAnchor;
  readonly momentum:      Record<string, unknown>;        // code-derived from p.0.prior
  readonly news?:         ReadonlyArray<LlmNewsItem>;
  readonly grounding?:    WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function tickerExcerpt(td: Record<string, unknown>): string {
  const fields = ['price', 'close', 'open', 'high', 'low', 'volume', 'beta', 'sector', 'industry'];
  const lines = fields
    .filter((f) => f in td)
    .map((f) => `  ${f}: ${fmt(td[f])}`);
  return lines.join('\n') || '  (no ticker data)';
}

function momentumExcerpt(m: Record<string, unknown>): string {
  // D40 (2026-05-28) renamed momentum's support/resistance scalars to a
  // `pivot` object {pp,s1,s2,r1,r2}; this whitelist still listed the dead
  // keys, so the analyst saw only the categorical factors.price_vs_levels
  // and none of the actual pivot levels. Render `pivot` so the analyst can
  // cite real S1/R1/PP price levels.
  // S-13: 'action' (ADD|HOLD|TRIM) removed — action verbs must NOT enter the STATE-ONLY
  // chain (feedback_llm_state_not_actions); the momentum STATE + pattern already inform.
  const fields = ['state', 'confidence', 'pattern', 'pivot', 'factors'];
  return fields
    .filter((f) => f in m)
    .map((f) => `  ${f}: ${fmt(m[f])}`)
    .join('\n') || '  (no momentum signals)';
}

function anchorBlock(a: TickerMarketAnchor): string {
  return [
    '  m.synthesis (PRIMARY anchor):',
    `    thesis_headline:   ${a.synthesis_anchor.thesis_headline}`,
    `    regime:            ${a.synthesis_anchor.regime} (confidence ${a.synthesis_anchor.regime_confidence})`,
    `    weights:           contrarian=${a.synthesis_anchor.contrarian_weight}, consensus=${a.synthesis_anchor.consensus_weight}, inductive=${a.synthesis_anchor.inductive_weight ?? 'null'}`,
    `    crux:              ${a.synthesis_anchor.crux}`,
    `    horizon:           ${a.synthesis_anchor.horizon}`,
    `    probability:       ${a.synthesis_anchor.probability}`,
    '  m.contrarian.refine (boundary scenario):',
    `    thesis_headline:   ${a.contrarian_anchor.thesis_headline}`,
    `    priced_explanation:${a.contrarian_anchor.priced_explanation}`,
    `    probability:       ${a.contrarian_anchor.probability}`,
    '  m.consensus.refine (boundary scenario):',
    `    thesis_headline:   ${a.consensus_anchor.thesis_headline}`,
    `    priced_explanation:${a.consensus_anchor.priced_explanation}`,
    `    probability:       ${a.consensus_anchor.probability}`,
  ].join('\n');
}

export function buildUserPrompt(input: PContrarianAnalystInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  return `${groundingBlock}TICKER: ${input.ticker}

TICKER DATA (price + meta):
${tickerExcerpt(input.ticker_data)}

MOMENTUM SIGNALS (code-derived from p.0.prior):
${momentumExcerpt(input.momentum)}

MARKET ANCHORS:
${anchorBlock(input.market_anchor)}

TICKER NEWS (last 48h, deduped + ranked):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_p_contrarian_analyst

Emit a SINGLE JSON object matching the schema. Predict THIS TICKER's directional move UNDER THE CONTRARIAN BOUNDARY SCENARIO from the market anchor. Cite ticker payload + momentum + market basis in mechanism. NO commentary outside JSON.`;
}
